// Single QueryClient instance shared across the app. Lives in its own module
// (not in App.tsx) to avoid circular imports — `lib/api.ts` needs to clear
// the cache on 401, and api.ts can't import from App.tsx without cycling.

import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';
import { toast } from 'sonner';

// Why duck-typed instead of `instanceof ApiError`: api.ts dynamically imports
// this module on 401, and pulling ApiError back the other direction creates
// a runtime circular dependency. Duck-typing on the `status` property is
// stable enough — ApiError is the only thing in the codebase that throws
// with a numeric status.
function isAuthOrMissingError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const status = (err as { status?: unknown }).status;
  return typeof status === 'number' && [401, 403, 404].includes(status);
}

// Surface a user-readable string from whatever the thrown error looks like.
// ApiError carries `.message` plus an optional `.status`; raw fetch/Error
// throws have `.message`; everything else falls back to a generic note.
function readableError(err: unknown, fallback: string): string {
  if (!err) return fallback;
  if (typeof err === 'string') return err;
  if (typeof err === 'object' && err !== null) {
    const msg = (err as { message?: unknown }).message;
    if (typeof msg === 'string' && msg.trim()) return msg;
  }
  return fallback;
}

export const queryClient = new QueryClient({
  // Global query error handler. Why this instead of per-query onError:
  // catches the long tail of fetches across 27+ files that today either
  // silently catch-and-fallback or have no handler at all. Result: failed
  // background reads now visibly toast instead of leaving the operator
  // staring at "no denials" when the API actually 500'd.
  queryCache: new QueryCache({
    onError: (error, query) => {
      // Auth flow handles 401/403; 404 means the resource is genuinely gone
      // and the consuming page should render its own empty state.
      if (isAuthOrMissingError(error)) return;
      // If the cache already has data, the user is looking at something
      // valid — toasting on a failed background refetch is noisier than
      // helpful. Only toast when the query has no data to show.
      if (query.state.data !== undefined) return;
      toast.error(readableError(error, 'Could not load data'), {
        description: 'Refresh the page or try again in a moment.',
      });
    },
  }),
  // Mutations almost always have a user action behind them (clicked Save,
  // Approve, Post Payment). A failed mutation that doesn't toast = user
  // thinks it worked. We only fire the global toast if the mutation
  // doesn't define its own onError — otherwise both fire and the user
  // sees two toasts for the same failure.
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) => {
      if (isAuthOrMissingError(error)) return;
      if (mutation.options.onError) return;
      toast.error(readableError(error, 'Action failed'));
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      // Skip retry for 401/403/404 — retrying an auth failure just delays
      // the redirect-to-login by 600ms and could expose stale cached data
      // to the new user. 5xx and network errors get one retry as before.
      retry: (failureCount, error) => {
        if (isAuthOrMissingError(error)) return false;
        return failureCount < 1;
      },
    },
  },
});
